import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTheme } from '../../../context/ThemeContext';
import { getCardBackground, getPrimaryText, getMutedText, getLightBorder, getInputStyles } from '../../../utils/themeUtils';
import {
  setMembership,
} from "../../../store/slices/membershipSlice";
import { getAllMemberships } from "../../../api/membershipAPI";

type UserType = {
  id: string;
  first_name: string;
  last_name?: string;
  email: string;
  type: "member" | "customer";
};

interface SearchBarProps {
  onMemberSelect: (member: any, memberId: string) => void;
  disabled?: boolean;
}

function MemberSearchBar({ onMemberSelect, disabled = false }: SearchBarProps) {
  const { isDarkMode } = useTheme();
  const [query, setQuery] = useState<string>("");
  const [suggestions, setSuggestions] = useState<UserType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useDispatch();
  // HELPER
  const token = localStorage.getItem("authToken");
  localStorage.getItem("authSource");

  const handleSelect = (item: UserType) => {
    // Members AND customers are both memberships rows — either can be
    // invoiced / charged (customers: leotards, merch, camp top-ups).
    dispatch(setMembership(item)); // Store selected member in Redux
    onMemberSelect(item, item.id); // Pass selected member ID to parent (for invoice)
    setSuggestions([]); // Optionally clear dropdown
    setQuery(""); // Optionally clear search input
  };

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (!query) {
      setSuggestions([]);
      return;
    }

    // debounce search
    timeoutRef.current = setTimeout(async () => {
      try {
        setLoading(true);

        // 🔑 Call API with searchTerm
        const response = await getAllMemberships(token, {
          page: 1,
          limit: 10, // keep small for autocomplete
          searchTerm: query,
        });

        if (response?.status === "success") {
          // Label rows with their real account type so staff can tell a
          // customer account from a member at a glance (previously every
          // row was hardcoded "member" even when it was a customer).
          const results: UserType[] = response.data.map((m: any) => ({
            ...m,
            type: m.account_type === "customer" ? "customer" : "member",
          }));

          setSuggestions(results);
        } else {
          setSuggestions([]);
        }
      } catch (err) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400); // debounce 400ms

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [query]);

  return (
    <div className="relative w-full">
      <input
        aria-label="Search name or email"
        type="text"
        placeholder="Search name or email..."
        className={`w-full border ${getLightBorder(isDarkMode)} p-2 rounded focus:outline-none focus:ring-2 focus:ring-[var(--cc-focus)] focus:border-[var(--cc-focus)] transition ${getInputStyles(isDarkMode)} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        value={query}
        onChange={(e) => !disabled && setQuery(e.target.value)}
        disabled={disabled}
      />
      {loading && (
        <div className={`absolute top-full mt-1 text-sm ${getMutedText(isDarkMode)}`}>
          Searching...
        </div>
      )}
      {suggestions?.length > 0 && (
        <ul className={`absolute ${getCardBackground(isDarkMode)} border ${getLightBorder(isDarkMode)} rounded shadow mt-1 z-10 w-full`}>
          {suggestions?.map((item) => (
            <li
              key={`${item?.type}-${item?.id}`}
              className={`p-2 hover:opacity-80 cursor-pointer`}
              onClick={() => handleSelect(item)}
            >
              <div className={`font-medium ${getPrimaryText(isDarkMode)}`}>
                {item?.first_name} {item?.last_name}
              </div>
              <div className={`text-sm ${getMutedText(isDarkMode)}`} title={item?.email}>
                {item?.email?.length > 20
                  ? `${item?.email?.slice(0, 20)}...`
                  : item?.email}{" "}
                ({item?.type})
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default MemberSearchBar;
